import dayjs, { type Dayjs } from "dayjs";
import type { Competition } from "./queries/competitions";
import { countdownIfNear } from "./competitionState";

/**
 * A competition's uzávěrky as the club reads them: one row per distinct
 * instant, naming everything that closes then.
 *
 * An organizer who sets only the uzávěrka přihlášek leaves the two change
 * deadlines and the uzávěrka hudby resolving to the same instant, and four
 * identical dates on a card read as noise rather than as four facts. So
 * rows merge by instant, and the label says what the one date closes.
 *
 * Reads only the server's RESOLVED values (`effective_*`) and never walks
 * the fallback chain itself — that chain is `competition.CategoryDeadline` /
 * `RosterDeadline` / `MusicDeadline` in Go, and a second copy of it here
 * would drift.
 *
 * Display only. `passed` and `countdown` style a row; nothing may gate an
 * action on them. What a club may still do is `entry_phase`,
 * `entry_change_window` and each entry's `permissions`, all answered by
 * the server (e2e/specs/prihlaska/contract.spec.ts).
 */

export type DeadlineKind = "entry" | "category" | "roster" | "music";

/** What an uzávěrka closes, from the glossary's „Uzávěrka …" terms. */
export const DEADLINE_KIND_LABELS: Record<DeadlineKind, string> = {
  entry: "přihlášky",
  category: "změny kategorie",
  roster: "změny soutěžících",
  music: "hudba",
};

export interface DeadlineGroup {
  at: Dayjs;
  /** In ladder order, music last. */
  kinds: DeadlineKind[];
  passed: boolean;
  /** Only on the next group still ahead, and only once it is near. */
  countdown: string | null;
}

export type CompetitionDeadlineFields = Pick<
  Competition,
  | "entry_deadline"
  | "effective_entry_category_deadline"
  | "effective_entry_roster_deadline"
  | "effective_music_deadline"
>;

export function deadlineGroups(
  c: CompetitionDeadlineFields,
  now: Dayjs = dayjs(),
): DeadlineGroup[] {
  const resolved: [DeadlineKind, Dayjs | undefined][] = [
    ["entry", c.entry_deadline],
    ["category", c.effective_entry_category_deadline],
    ["roster", c.effective_entry_roster_deadline],
    ["music", c.effective_music_deadline],
  ];
  const byInstant = new Map<number, DeadlineGroup>();
  for (const [kind, at] of resolved) {
    if (!at) continue;
    const group = byInstant.get(at.valueOf());
    if (group) {
      group.kinds.push(kind);
    } else {
      byInstant.set(at.valueOf(), { at, kinds: [kind], passed: at.isBefore(now), countdown: null });
    }
  }
  const groups = [...byInstant.values()].sort((a, b) => a.at.valueOf() - b.at.valueOf());
  // One countdown per list: beside every row it stops pointing at the one
  // that matters.
  const next = groups.find((g) => !g.passed);
  if (next) next.countdown = countdownIfNear(next.at, now);
  return groups;
}

/** „Přihlášky, změny kategorie a hudba". */
export function deadlineGroupLabel(kinds: DeadlineKind[]): string {
  const words = kinds.map((k) => DEADLINE_KIND_LABELS[k]);
  const text =
    words.length <= 1
      ? (words[0] ?? "")
      : `${words.slice(0, -1).join(", ")} a ${words[words.length - 1]}`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
